import { Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom';
import { KolLoginPage } from './pages/KolLoginPage';
import { VerifyPage } from './pages/VerifyPage';
import { UploadDocPage } from './pages/UploadDocPage';
import { OcrReviewPage } from './pages/OcrReviewPage';
import { DocumentDetailPage } from './pages/DocumentDetailPage';
import { ObdDiagnosticsPage } from './pages/ObdDiagnosticsPage';
import { KolHomePage } from './pages/KolHomePage';
import { KolCarPage } from './pages/KolCarPage';
import { KolAskPage } from './pages/KolAskPage';
import { KolDocsPage } from './pages/KolDocsPage';
import { GlobalDocsPage } from './pages/GlobalDocsPage';
import { PermissionsPage } from './pages/PermissionsPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AccidentFlowPage } from './pages/AccidentFlowPage';
import { BillingPage, CheckoutSuccessPage } from './pages/BillingPage';
import { FamilyPage } from './pages/FamilyPage';
import { OwnerKbPage } from './pages/OwnerKbPage';
import { RequireAuth } from './components/RequireAuth';

/**
 * Redirect the orphaned legacy car routes to their קוֹל (Forest) equivalents.
 * /vehicles/:id and /vehicles/:id/ask are strictly-worse duplicates of
 * KolCarPage / KolAskPage; preserve the :id and any query string (e.g. ?q=).
 */
function RedirectToKol({ suffix = '' }: { suffix?: string }) {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/kol/vehicles/${id}${suffix}${search}`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Anonymous */}
      <Route path="/login" element={<KolLoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />

      {/* Grantee invite landing (requires auth, handled inside the page) */}
      <Route path="/permissions/accept" element={<AcceptInvitePage />} />

      {/* Owner surfaces. קוֹל (Voice) is now the app: / and /kol both render
          the Voice Home; the deeper legacy utility pages (OBD, KB, billing,
          permissions, accident) remain reachable from the קוֹל surfaces. */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <KolHomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/kol"
        element={
          <RequireAuth>
            <KolHomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/kol/vehicles/:id"
        element={
          <RequireAuth>
            <KolCarPage />
          </RequireAuth>
        }
      />
      <Route
        path="/kol/vehicles/:id/ask"
        element={
          <RequireAuth>
            <KolAskPage />
          </RequireAuth>
        }
      />
      <Route
        path="/kol/documents"
        element={
          <RequireAuth>
            <GlobalDocsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/kol/vehicles/:id/documents"
        element={
          <RequireAuth>
            <KolDocsPage />
          </RequireAuth>
        }
      />
      {/* Orphaned legacy car page -> קוֹל Car */}
      <Route path="/vehicles/:id" element={<RedirectToKol />} />
      <Route
        path="/vehicles/:id/permissions"
        element={
          <RequireAuth>
            <PermissionsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/upload/registration"
        element={
          <RequireAuth>
            <UploadDocPage />
          </RequireAuth>
        }
      />
      <Route
        path="/documents/:id/review"
        element={
          <RequireAuth>
            <OcrReviewPage />
          </RequireAuth>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <RequireAuth>
            <DocumentDetailPage />
          </RequireAuth>
        }
      />
      {/* Orphaned legacy Ask page -> קוֹל Ask */}
      <Route path="/vehicles/:id/ask" element={<RedirectToKol suffix="/ask" />} />
      <Route
        path="/vehicles/:id/accident"
        element={
          <RequireAuth>
            <AccidentFlowPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vehicles/:id/obd"
        element={
          <RequireAuth>
            <ObdDiagnosticsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/vehicles/:id/kb"
        element={
          <RequireAuth>
            <OwnerKbPage />
          </RequireAuth>
        }
      />
      <Route
        path="/billing"
        element={
          <RequireAuth>
            <BillingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/billing/success"
        element={
          <RequireAuth>
            <CheckoutSuccessPage />
          </RequireAuth>
        }
      />
      <Route
        path="/family"
        element={
          <RequireAuth>
            <FamilyPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
